import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	performGetRequest,
	performPostRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { Action } from '@atlassian/react-sweet-state';
import type { Page, IssueTypeScreenScheme } from '../../../common/types';
import type {
	IssueTypeScreenSchemesServiceState,
	IssueTypeScreenSchemesServiceContainerProps,
	IssueTypeScreenSchemesFilter,
	CreateIssueTypeScreenSchemeRequest,
	CreateIssueTypeScreenSchemeResponse,
	ErrorResponse,
} from '../types';
import {
	defaultFilter,
	buildFetchIssueTypeScreenSchemesUrl,
	buildCreateIssueTypeScreenSchemesUrl,
} from './utils';

const fetchIssueTypeScreenSchemes =
	(
		filter: IssueTypeScreenSchemesFilter = defaultFilter,
	): Action<IssueTypeScreenSchemesServiceState, IssueTypeScreenSchemesServiceContainerProps> =>
	async ({ setState, getState }) => {
		setState({
			issueTypeScreenSchemes: { ...getState().issueTypeScreenSchemes, isLoading: true },
			filter,
		});
		try {
			const response: Page<IssueTypeScreenScheme> = await performGetRequest(
				buildFetchIssueTypeScreenSchemesUrl(filter),
			);
			setState({
				issueTypeScreenSchemes: {
					values: response.values,
					maxResults: response.maxResults,
					startAt: response.startAt,
					total: response.total,
					isLast: response.isLast,
					isLoading: false,
					isError: false,
					error: undefined,
				},
				filter,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setState({
				issueTypeScreenSchemes: {
					...getState().issueTypeScreenSchemes,
					isLoading: false,
					error,
					isError: true,
				},
				filter,
			});
			fireErrorAnalytics({
				error,
				meta: {
					id: 'fetchIssueTypeScreenSchemes',
					packageName: 'jiraAdminScreensServices',
					teamName: 'morphinecake',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};
const createIssueTypeScreenScheme =
	(
		createIssueTypeScreenSchemeRequest: CreateIssueTypeScreenSchemeRequest,
		onSuccess: (issueTypeScreenSchemeId: CreateIssueTypeScreenSchemeResponse) => void,
		onFailure: (errorMessages: ErrorResponse) => void,
	): Action<IssueTypeScreenSchemesServiceState, IssueTypeScreenSchemesServiceContainerProps> =>
	async ({ setState, getState, dispatch }) => {
		setState({
			issueTypeScreenSchemes: { ...getState().issueTypeScreenSchemes, isLoading: true },
		});
		try {
			const response: CreateIssueTypeScreenSchemeResponse = await performPostRequest(
				buildCreateIssueTypeScreenSchemesUrl(),
				{
					body: JSON.stringify({
						name: createIssueTypeScreenSchemeRequest.schemeName,
						description: createIssueTypeScreenSchemeRequest.schemeDescription,
						issueTypeMappings: [
							{
								issueTypeId: 'default',
								screenSchemeId: createIssueTypeScreenSchemeRequest.fieldScreenSchemeId,
							},
						],
					}),
				},
			);
			dispatch(fetchIssueTypeScreenSchemes({ ...defaultFilter, ...getState().filter }));
			onSuccess(response);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'createIssueTypeScreenScheme',
					packageName: 'jiraAdminScreensServices',
					teamName: 'morphinecake',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			onFailure(error);
		} finally {
			setState({
				issueTypeScreenSchemes: { ...getState().issueTypeScreenSchemes, isLoading: false },
			});
		}
	};

const actions = {
	fetchIssueTypeScreenSchemes,
	createIssueTypeScreenScheme,
} as const;

export default actions;
